import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Typography from '@mui/material/Typography';
import { type PropsWithChildren, useEffect, useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';

import { CloverPaymentInformation } from '@getbento/payments/components/clover/CloverPaymentInformation';
import { useModal } from 'reservations/base/providers/ModalProvider';
import { useBookingPaymentMethod } from 'reservations/booking/providers/BookingProvider';
import { RequiredPaymentFeeNotice } from 'reservations/feePolicy/components/RequiredPaymentFeeNotice';
import { SavedPaymentMethodLabel } from 'reservations/payments/components/SavedPaymentMethodLabel';
import { useDinerPaymentMethods } from 'reservations/payments/hooks/useDinerPaymentMethods';

export const RequiredPaymentModify = ({ children }: PropsWithChildren) => {
  const { id: bookingPaymentMethodId } = useBookingPaymentMethod();
  const { defaultPaymentMethodId } = useDinerPaymentMethods();
  return (
    <RequiredPayment
      savedPaymentMethodId={bookingPaymentMethodId ?? defaultPaymentMethodId}
    >
      {children}
    </RequiredPayment>
  );
};

export const RequiredPaymentCreate = ({ children }: PropsWithChildren) => {
  const { defaultPaymentMethodId } = useDinerPaymentMethods();
  return (
    <RequiredPayment savedPaymentMethodId={defaultPaymentMethodId}>
      {children}
    </RequiredPayment>
  );
};

interface Props {
  savedPaymentMethodId?: string;
}

export const RequiredPayment = ({
  savedPaymentMethodId,
  children
}: PropsWithChildren<Props>) => {
  const { control, setValue } = useFormContext();
  const [date, savedPaymentMethod] = useWatch({
    name: ['date', 'savedPaymentMethod']
  });
  const [replaceCard, setReplaceCard] = useState(false);
  const { paymentMethods: dinerPaymentMethods } = useDinerPaymentMethods();

  const {
    modalContext: { cardDeclinedError }
  } = useModal();

  useEffect(() => {
    if (savedPaymentMethodId && !cardDeclinedError) {
      setValue('savedPaymentMethod', savedPaymentMethodId);
    }
    if (cardDeclinedError) {
      setValue('token', '');
      setValue('cardDetails', null);
    }
  }, [savedPaymentMethodId]);

  // TODO: Update this in the future when we support multiple payment methods
  const paymentMethodToRender = dinerPaymentMethods.find(
    ({ id }) => id === savedPaymentMethodId
  );
  const paymentMethods = paymentMethodToRender ? [paymentMethodToRender] : [];

  return (
    <Box>
      <Box mb="10px">
        <Typography
          sx={{
            fontWeight: 600,
            mb: '5px'
          }}
        >
          Credit card required
        </Typography>
        <RequiredPaymentFeeNotice />
      </Box>
      {paymentMethods.length > 0 && (
        <Box mb="20px" width="100%">
          <Controller
            control={control}
            name="savedPaymentMethod"
            render={({ field: { onChange, value, ...rest } }) => (
              <FormControl sx={{ width: '100%' }}>
                <RadioGroup
                  {...rest}
                  onChange={(e, selectedPaymentMethod) => {
                    setValue('savedPaymentMethod', selectedPaymentMethod);
                    setValue('token', '');
                    setValue('cardDetails', null);
                    setReplaceCard(false);
                    onChange(e, selectedPaymentMethod);
                  }}
                  value={value}
                >
                  {paymentMethods.map(
                    ({ isExpirationValid, id, ...paymentMethod }) => (
                      <FormControlLabel
                        sx={{
                          '.MuiFormControlLabel-label': {
                            width: '100%'
                          }
                        }}
                        key={id}
                        disabled={!isExpirationValid}
                        value={id}
                        control={<Radio />}
                        label={
                          <SavedPaymentMethodLabel
                            {...paymentMethod}
                            isExpirationValid={isExpirationValid}
                          />
                        }
                      />
                    )
                  )}
                  <FormControlLabel
                    value=""
                    control={<Radio />}
                    onClick={() => setReplaceCard(true)}
                    label="Replace your card"
                  />
                </RadioGroup>
              </FormControl>
            )}
          />
        </Box>
      )}
      {!savedPaymentMethod || replaceCard ? (
        <Box>
          <CloverPaymentInformation bookingDate={date}>
            {children}
          </CloverPaymentInformation>
        </Box>
      ) : (
        children
      )}
    </Box>
  );
};
